import type { ReactNode } from 'react';

import { extractApiErrorMessage } from '@/shared/lib/errors/extractApiErrorMessage';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { FieldGroup } from '@/shared/ui/shadcn/field';

export type DetailActionVariant = 'default' | 'destructive';

export interface DetailActionFormProps {
  readonly mutation: {
    readonly isPending: boolean;
    readonly isError: boolean;
    readonly error: unknown;
  };
  readonly onSubmit: () => void | Promise<void>;
  readonly submitLabel: string;
  readonly pendingLabel: string;
  readonly submitVariant?: DetailActionVariant;
  readonly children: ReactNode;
}

/**
 * Shared scaffolding for detail-sheet action forms (refill/RxChange
 * approve, deny, reconcile, change-patient). Renders an error banner when
 * the mutation last failed, wraps fields in a ``FieldGroup``, and emits a
 * pending-aware submit button. The submit handler short-circuits while
 * ``mutation.isPending`` to prevent double-submits.
 */
export function DetailActionForm({
  mutation,
  onSubmit,
  submitLabel,
  pendingLabel,
  submitVariant = 'default',
  children,
}: DetailActionFormProps) {
  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (mutation.isPending) return;
        void onSubmit();
      }}
      className="flex flex-col gap-3"
    >
      {mutation.isError ? (
        <Alert variant="destructive">
          <AlertDescription>{extractApiErrorMessage(mutation.error)}</AlertDescription>
        </Alert>
      ) : null}

      <FieldGroup>{children}</FieldGroup>

      <div className="flex justify-end">
        <Button type="submit" variant={submitVariant} disabled={mutation.isPending}>
          {mutation.isPending ? pendingLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
}
